import { UnregisterCallback } from "history";
import { isEqual } from "lodash";
import create from "zustand";
import { Facet } from "@linkfluence/models/facet";
import { extractCleanedClause } from "@radarly/models/clause/extractor";
import {
  getInnerClauseFromClause,
  getQueryFromClause,
} from "@radarly/models/clause/getter";
import { areClausesEqual } from "@radarly/models/clause/is";
import { Query } from "@radarly/models/queries";
import { EditionQueryFields, SearchType } from "@radarly/settings/models/query";
import { createSelectionStore } from "../selectionFactory";
import { updateQuery } from "./editor";

type QuerySectionStore = {
  section: string;
  reset: () => void;
  set: (section: string) => void;
};

type QueryEditionInfo = {
  isQueryEdited: boolean;
  isShortQueryEdited: boolean;
  isSinaweiboQueryEdited: boolean;
  searchType: SearchType;
  skipHasChangesModal: UnregisterCallback;
};

export const useQuerySectionStore = create<QuerySectionStore>()((_set) => ({
  section: null,
  reset: () => _set(() => ({ section: null })),
  set: (section) => _set(() => ({ section })),
}));

// Query used in the Query details page to hold all user changes until they are saved
export const queryDraft = create<Query>()(() => null);

export const hasSearchQueryChanged = (query) => {
  const _queryDraft = queryDraft.getState();
  return !isEqual(
    getQueryFromClause(extractCleanedClause(_queryDraft?.filter?.clause)),
    getQueryFromClause(extractCleanedClause(query?.filter?.clause))
  );
};

// Temporary information used during the query edition
export const useQueryEditionInfo = create<QueryEditionInfo>()(() => null);

// Queries selected in query list
export const useQuerySelectionStore = createSelectionStore<Query>();

export const initQueryDraft = (query: Query) => {
  const selectedBrandsIds = getInnerClauseFromClause(
    query?.filter?.clause,
    Facet.GlobalTags
  );

  useQueryEditionInfo.setState({
    isQueryEdited: false,
    isShortQueryEdited: false,
    isSinaweiboQueryEdited: false,
    searchType: selectedBrandsIds?.[Facet.GlobalTags]?.length
      ? "brands"
      : "standard",
  });
  return queryDraft.setState(query);
};

export const updateQueryDraft = <KeyType extends keyof EditionQueryFields>(
  key: KeyType,
  value: EditionQueryFields[KeyType]
) => {
  const prevQuery = queryDraft.getState();
  const newQuery = updateQuery(key, value, prevQuery);
  if (value) {
    useQueryEditionInfo.setState(() => ({ isQueryEdited: true }));
  }
  return queryDraft.setState(newQuery);
};

export const updateQueryEditionInfo = <KeyType extends keyof QueryEditionInfo>(
  fieldEdited: KeyType,
  value: QueryEditionInfo[KeyType]
) => {
  return useQueryEditionInfo.setState((prev) => ({
    ...prev,
    [fieldEdited]: value,
  }));
};

export const hasPostQueryChanged = (query: Query) => {
  const _query = queryDraft.getState();
  return (
    !isEqual(query?.parents, _query?.parents) ||
    !isEqual(query?.isSource, _query?.isSource) ||
    !areClausesEqual(
      extractCleanedClause(query?.filter?.clause),
      extractCleanedClause(_query?.filter?.clause)
    )
  );
};

export const resetQueryDraft = () => {
  queryDraft.setState(null, true);
  useQueryEditionInfo.setState(null, true);
};
